import { useEffect } from "react";
import { Box, Typography, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchProcurementHealthInsights,
  fetchProcurementHealthDashboard,
} from "../../../store/contractDashboardSlice";
import Sidebar from "../../../components/Sidebar";
import InsightsCard from "../../../components/ContractDashboard/InsightsCard";
import MetricsCard from "../../../components/ContractDashboard/MetricsCard";
import FinancialSummary from "../../../components/ContractDashboard/FinancialSummary";
import ContractType from "../../../components/ContractDashboard/ContractType";
import CompetitiveIntensity from "../../../components/ContractDashboard/CompetitiveIntensity";
import SpendUnderManagement from "../../../components/ContractDashboard/SpendUnderManagement";
import PRComplianceRate from "../../../components/ContractDashboard/PRComplianceRate";
import TouchlessPRNPS from "../../../components/ContractDashboard/TouchlessPRNPS";
import SupplierRiskScore from "../../../components/ContractDashboard/SupplierRiskScore";
import PageLoader from "../../../components/PageLoader";
import styles from "./ProcurementHealth.module.scss";
import DashboardFilters from "../../../components/ContractDashboard/DashboardFilters";

const ProcurementHealth = () => {
  const dispatch = useAppDispatch();
  const {
    procurementHealthInsights,
    procurementHealthDashboard,
    procurementHealthInsightsLoading,
    procurementHealthDashboardLoading,
    error,
  } = useSelector((state: RootState) => state.contractDashboard);
  const { contractDashboardFilters } = useSelector(
    (state: RootState) => state.filters,
  );

  useEffect(() => {
    const filters = {
      group_of_companies: contractDashboardFilters.groupCompany,
      contract_type: contractDashboardFilters.contractType,
    };

    dispatch(fetchProcurementHealthInsights(filters));
    dispatch(fetchProcurementHealthDashboard(filters));
  }, [
    dispatch,
    contractDashboardFilters.groupCompany,
    contractDashboardFilters.contractType,
  ]);

  // Map API insight type to InsightsCard type
  const getCardType = (
    insightType: string,
  ): "risk" | "leakage" | "bottleneck" => {
    const typeMap: Record<string, "risk" | "leakage" | "bottleneck"> = {
      warning: "risk",
      info: "leakage",
      success: "bottleneck",
    };
    return typeMap[insightType] || "risk";
  };

  // Aggregate loading: Show loader until ALL APIs complete successfully
  const isLoading =
    (procurementHealthInsightsLoading || procurementHealthDashboardLoading) &&
    (procurementHealthInsights === null ||
      procurementHealthDashboard === null);

  if (isLoading) {
    return <PageLoader label="Loading procurement health..." />;
  }

  if (error) {
    return (
      <Box className={styles.landingContainer}>
        <Box className={styles.contentWrapper}>
          <Sidebar activeNav="dashboard" />
          <Box className={styles.mainContent}>
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  if (
    procurementHealthInsights === null ||
    procurementHealthDashboard === null
  ) {
    return null;
  }

  // Ensure insights is an array
  const insightsArray = Array.isArray(procurementHealthInsights)
    ? procurementHealthInsights
    : [];

  return (
    <Box className={styles.landingContainer}>
      <Box className={styles.contentWrapper}>
        <Sidebar activeNav="dashboard" />
        <Box className={styles.mainContent}>
          <Box className={styles.dashboardContainer}>
            <DashboardFilters
              title="Procurement Health"
              subtitle="Overall health of procurement across suppliers, sourcing, and contracts."
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {insightsArray.length > 0 && (
              <Box className={styles.insightsSection}>
                <Typography variant="h5" className={styles.sectionTitle}>
                  Insights
                </Typography>
                <Box className={styles.insightsGrid}>
                  {insightsArray.map((insight) => (
                    <InsightsCard
                      key={insight.title}
                      type={getCardType(insight.type)}
                      title={insight.title}
                      description={insight.message}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box className={styles.metricsSection}>
              <Box className={styles.metricsGrid}>
                <MetricsCard
                  title="Total Active Contracts"
                  value={procurementHealthDashboard.contract_statistics.active_contracts.count.toString()}
                  amount={`$${procurementHealthDashboard.contract_statistics.active_contracts.active_contracts_value.toFixed(1)}M`}
                  percentage={
                    procurementHealthDashboard.contract_statistics
                      .active_contracts.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Expiring in 0-15 days"
                  value={procurementHealthDashboard.contract_statistics.expiring_contracts.count.toString()}
                  amount={`$${procurementHealthDashboard.contract_statistics.expiring_contracts.expiring_contracts_value.toFixed(1)}M`}
                  percentage={
                    procurementHealthDashboard.contract_statistics
                      .expiring_contracts.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Expired Contracts"
                  value={procurementHealthDashboard.contract_statistics.expired_contracts.count.toString()}
                  amount={`$${procurementHealthDashboard.contract_statistics.expired_contracts.expired_contracts_value.toFixed(1)}K`}
                  percentage={
                    procurementHealthDashboard.contract_statistics
                      .expired_contracts.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="UAE Jurisdiction"
                  value={procurementHealthDashboard.contract_statistics.uae_jurisdiction.count.toString()}
                  amount={`${procurementHealthDashboard.contract_statistics.uae_jurisdiction.percentage}% of total`}
                  percentage={
                    procurementHealthDashboard.contract_statistics
                      .uae_jurisdiction.change
                  }
                  percentageLabel="vs previous quarter"
                />
              </Box>
            </Box>

            <Box className={styles.chartsSection}>
              <FinancialSummary
                data={procurementHealthDashboard.widgets.costsAndRevenues}
              />
              <ContractType
                data={procurementHealthDashboard.widgets.contractTypeSummary}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <CompetitiveIntensity
                data={procurementHealthDashboard.widgets.competitiveIntensity.data}
              />
              <SpendUnderManagement
                data={procurementHealthDashboard.widgets.spendUnderManagement}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <PRComplianceRate
                data={procurementHealthDashboard.widgets.prComplianceRate.data}
              />
              <TouchlessPRNPS
                data={procurementHealthDashboard.widgets.touchlessPrRateScore}
              />
            </Box>

            <Box className={styles.tableSection}>
              <SupplierRiskScore
                data={procurementHealthDashboard.supplier_risk_scores}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcurementHealth;
